import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../Styles/StudentUploadMark.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

const StudentUploadMark = () => {
  const [testNames, setTestNames] = useState([]);
  const [allExams, setAllExams] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [testName, setTestName] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [subject, setSubject] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalMarks, setTotalMarks] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  useEffect(() => {
    const fetchExamDropdownData = async () => {
      setInitialLoading(true);
      const authToken = localStorage.getItem("authToken");
      try {
        const response = await axios.get(
          "https://api.uniums.com/api/exam/get-exam-dropdown-data",
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setTestNames(response.data.examNames || []);
        setAllExams(response.data.exams || []);
        
        // Store original data but don't populate dropdowns until selections are made
        setTestNames(response.data.examNames || []);
      } catch (error) {
        console.error("Error fetching exam dropdown data:", error);
        setError("Failed to load dropdown data");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchExamDropdownData();
  }, []);

  // Update available classes when test name changes
  useEffect(() => {
    if (!testName) {
      setAvailableClasses([]);
      setSelectedClass("");
      return;
    }
  
    const authToken = localStorage.getItem("authToken");
    const fetchClassesForExam = async () => {
      setClassesLoading(true);
      try {
        const response = await axios.get(
          `https://api.uniums.com/api/exam/get-classes-by-exam?examName=${encodeURIComponent(testName)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        setAvailableClasses(response.data.classes || []);
        setSelectedClass("");
      } catch (error) {
        console.error("Error fetching classes for exam:", error);
        setError("Failed to load classes for selected exam");
        setAvailableClasses([]);
      } finally {
        setClassesLoading(false);
      }
    };
  
    fetchClassesForExam();
  }, [testName]);

  // Update available subjects when class changes
  useEffect(() => {
    if (!testName || !selectedClass) {
      setAvailableSubjects([]);
      setSubject("");
      return;
    }

    const authToken = localStorage.getItem("authToken");
    const fetchSubjectsForClass = async () => {
      setSubjectsLoading(true);
      try {
        const response = await axios.get(
          `https://api.uniums.com/api/exam/get-subjects-by-exam-class?examName=${testName}&className=${selectedClass}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        
        setAvailableSubjects(response.data.subjects || []);
        setSubject("");
      } catch (error) {
        console.error("Error fetching subjects for class:", error);
        setError("Failed to load subjects for selected class");
        setAvailableSubjects([]);
      } finally {
        setSubjectsLoading(false);
      }
    };

    fetchSubjectsForClass();
  }, [testName, selectedClass]);

  useEffect(() => {
    if (!testName || !selectedClass || !subject) return;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      const authToken = localStorage.getItem("authToken");

      try {
        // First check if there are existing marks
        const existingMarksResponse = await axios.get(
          `https://api.uniums.com/api/exam/get-uploaded-marks?examName=${testName}&className=${selectedClass}&subject=${subject}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (existingMarksResponse.data.students.length > 0) {
          setStudents(existingMarksResponse.data.students);
          setIsUpdate(true);
        } else {
          // If no existing marks, fetch students list
          const studentsResponse = await axios.get(
            `https://api.uniums.com/api/exam/filter-students?className=${selectedClass}&subject=${subject}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          setStudents(studentsResponse.data.students || []);
          setIsUpdate(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [testName, selectedClass, subject]);

  const handleMarksChange = (index, value) => {
    const numericValue = Number(value);
    if (numericValue > totalMarks) {
      toast.error(`Obtained marks cannot be greater than ${totalMarks}`);
      return;
    }

    if (numericValue < 0) {
      toast.error("Obtained marks cannot be negative");
      return;
    }

    const updatedStudents = students.map((student, i) =>
      i === index
        ? {
            ...student,
            obtainedMarks: numericValue,
            percentage: (numericValue && totalMarks && !isNaN(numericValue) && !isNaN(totalMarks))
              ? ((numericValue / totalMarks) * 100).toFixed(2)
              : "0.00",
          }
        : student
    );
    setStudents(updatedStudents);
  };

  const handleSubjectChange = (selectedSubj) => {
    setSubject(selectedSubj);
    // Find the selected subject to get totalMarks
    const foundSubject = availableSubjects.find((s) => s.subject === selectedSubj);
    setTotalMarks(foundSubject ? foundSubject.totalMarks : 100);
  };

  const handleSubmit = () => {
    if (students.some((student) => student.obtainedMarks === undefined || student.obtainedMarks === "")) {
      toast.error("Please fill marks for all students before submitting.");
      return;
    }
    setShowModal(true);
  };

  const confirmSubmission = async () => {
    setShowModal(false);
    setLoading(true);
    const authToken = localStorage.getItem("authToken");
    const payload = {
      examName: testName,
      className: selectedClass,
      subject,
      students: students.map(student => ({
        ...student,
        studentId: student._id,
        totalMarks: totalMarks,
      })),
    };

    try {
      if (isUpdate) {
        await axios.put(
          "https://api.uniums.com/api/exam/update-marks",
          payload,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Marks updated successfully!");
      } else {
        await axios.post(
          "https://api.uniums.com/api/exam/upload-marks",
          payload,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Marks submitted successfully!");
      }
    } catch (error) {
      console.error("Error submitting marks:", error);
      toast.error(`Failed to ${isUpdate ? 'update' : 'submit'} marks. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestNameChange = (selectedTestName) => {
    setTestName(selectedTestName);
    setSelectedClass("");
    setSubject("");
    setStudents([]);
  };

  const handleClassChange = (selectedClassValue) => {
    setSelectedClass(selectedClassValue);
    setSubject("");
    setStudents([]);
  };

  return (
    <div className="student-upload-container">
      <h1>{isUpdate ? 'Update' : 'Upload'} Students Marks</h1>

      <div className="dropdown-container">
        {initialLoading ? (
          <div className="dropdown-loader"><Loader /></div>
        ) : (
          <select onChange={(e) => handleTestNameChange(e.target.value)} value={testName}>
            <option value="">Select Test Name</option>
            {testNames.map((name, index) => (
              <option key={index} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}

        {classesLoading ? (
          <div className="dropdown-loader"><Loader /></div>
        ) : (
          <select 
            onChange={(e) => handleClassChange(e.target.value)} 
            value={selectedClass}
            disabled={!testName}
          >
            <option value="">Select Class</option>
            {availableClasses.map((cls, index) => (
              <option key={index} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        )}

        {subjectsLoading ? (
          <div className="dropdown-loader"><Loader /></div>
        ) : (
          <select 
            onChange={(e) => handleSubjectChange(e.target.value)} 
            value={subject}
            disabled={!selectedClass}
          >
            <option value="">Select Subject</option>
            {availableSubjects.map((subj, index) => (
              <option key={index} value={subj.subject}>
                {subj.subject}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <div className="main-loader"><Loader /></div>}

      <div className="mark_upload_table">
        {testName && selectedClass && subject && students.length > 0 && !loading && (
          <table className="marks-table">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Name</th>
                <th>User Code</th>
                <th>Subject</th>
                <th>Obtained Marks</th>
                <th>Total Marks</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => (
                <tr key={student.employeeCode}>
                  <td>{index + 1}</td>
                  <td>{student.name}</td>
                  <td>{student.employeeCode}</td>
                  <td>{subject}</td>
                  <td>
                    <input
                      type="number"
                      placeholder="Enter Marks"
                      min="0"
                      max={totalMarks}
                      value={student.obtainedMarks || ""}
                      onChange={(e) => handleMarksChange(index, e.target.value)}
                    />
                  </td>
                  <td>{totalMarks}</td>
                  <td>{student.percentage || "0.00"}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {testName && selectedClass && subject && students.length > 0 && !loading && (
        <button className="submit_mark" onClick={handleSubmit} disabled={loading}>
          {isUpdate ? 'Update' : 'Submit'} Marks
        </button>
      )}

      {showModal && (
        <div className="modal_container_bg">
        <div className="modal_container_m">
          <p>Are you sure you want to {isUpdate ? 'update' : 'submit'} the marks?</p>
          <button onClick={confirmSubmission} className="ok_btn">OK</button>
          <button onClick={() => setShowModal(false)} className="cancel">Cancel</button>
        </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default StudentUploadMark;